import React, { useEffect, useState } from "react";
import {
  Camera,
  AlertTriangle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Info,
  ChevronDown,
  ChevronRight,
  Car,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MetricCard } from "@/components/common/metric-card";
import { GlobalFilterBar } from "@/components/common/global-filter-bar";
import { formatNumber } from "@/lib/utils";
import { formatDateForAPI } from "@/lib/date-utils";
import { cameraAuditsAPI } from "@/api";
import { useDateRange } from "@/contexts/DateRangeContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

interface AuditSummary {
  period: { start: string; end: string };
  total_cameras: number;
  cameras_with_issues: number;
  total_frequent_parker_misses: number;
  total_one_way_plates: number;
  orphan_concentration: string;
  orphan_concentration_detail: string;
  top_insight: string;
  total_orphans_in_period: number;
  unique_orphan_plates: number;
}

interface CameraHealth {
  camera_id: string;
  zone: string;
  camera_type: string;
  total_misses: number;
  unique_plates_affected: number;
  weekly_trend: Array<{
    week: string;
    misses: number;
    plates_affected: number;
  }>;
  status: string;
  change_detected: string | null;
  interpretation: string;
  example_plates: string[];
}

interface OneWayReport {
  zone: string;
  pattern: string;
  unique_plates: number;
  total_records: number;
  interpretation: string;
  example_plates: string[];
}

export function CameraAudits() {
  const { dateRange, selectedLocations } = useDateRange();
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [cameraHealth, setCameraHealth] = useState<CameraHealth[]>([]);
  const [oneWayReports, setOneWayReports] = useState<OneWayReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCameras, setExpandedCameras] = useState<Set<string>>(
    new Set(),
  );
  const [showMethodology, setShowMethodology] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const params = {
          start_date: formatDateForAPI(dateRange.from),
          end_date: formatDateForAPI(dateRange.to),
          location:
            selectedLocations.length > 0
              ? selectedLocations.join(",")
              : undefined,
        };

        const [summaryRes, healthRes, oneWayRes] = await Promise.all([
          cameraAuditsAPI.getSummary(params),
          cameraAuditsAPI.getCameraHealth({ ...params, min_misses: 50 }),
          cameraAuditsAPI.getOneWayPlates({ ...params, min_records: 5 }),
        ]);

        setSummary(summaryRes.data || null);
        setCameraHealth(healthRes.data || []);
        setOneWayReports(oneWayRes.data || []);
      } catch (error) {
        console.error("Failed to fetch camera audit data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange, selectedLocations]);

  const toggleCamera = (cameraKey: string) => {
    setExpandedCameras((prev) => {
      const next = new Set(prev);
      if (next.has(cameraKey)) {
        next.delete(cameraKey);
      } else {
        next.add(cameraKey);
      }
      return next;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "critical":
        return "text-red-500";
      case "degraded":
        return "text-amber-500";
      case "healthy":
        return "text-emerald-500";
      default:
        return "text-muted-foreground";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "critical":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Critical
          </span>
        );
      case "degraded":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">
            <TrendingDown className="w-3 h-3 mr-1" />
            Degraded
          </span>
        );
      case "healthy":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            Healthy
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Camera Audits</h1>
          <p className="text-muted-foreground">
            Equipment health analysis using vehicle behavior patterns
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowMethodology(!showMethodology)}
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          How This Works
        </Button>
      </div>

      {/* Methodology Explanation */}
      {showMethodology && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-primary" />
              How We Identify Camera Issues
            </h3>
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">The Challenge:</strong> When
                a vehicle enters but never exits (or vice versa), we can&apos;t
                directly know which camera failed. The entry camera worked fine,
                the exit camera worked fine for other cars, one of them just
                missed this plate.
              </p>
              <p>
                <strong className="text-foreground">Our Solution:</strong> We
                look at &quot;frequent parkers&quot; - vehicles with 10+
                successful sessions that always use the same entry/exit camera
                pattern. If plate ABC123 normally enters via Camera 1 and exits
                via Camera 2, but sometimes only shows an entry, we know Camera
                2 missed that exit.
              </p>
              <p>
                <strong className="text-foreground">One-Way Plates:</strong>{" "}
                Vehicles seen only entering OR only exiting (never both) may
                indicate wrong-way driving, camera blind spots, or unmonitored
                access points.
              </p>
              <p>
                <strong className="text-foreground">Important:</strong> Camera
                issues can change over time - equipment gets repositioned,
                cleaned, or degrades. The weekly trends show WHEN problems
                started, not just totals.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <GlobalFilterBar />

      {/* Top Insight Banner */}
      {summary?.top_insight && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-medium text-amber-500">Key Finding</h4>
                <p className="text-sm text-muted-foreground">
                  {summary.top_insight}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Cameras"
          value={summary?.total_cameras || 0}
          description="Monitored equipment"
          icon={Camera}
          loading={isLoading}
        />
        <MetricCard
          title="Cameras with Issues"
          value={summary?.cameras_with_issues || 0}
          description="100+ missed reads"
          icon={AlertTriangle}
          loading={isLoading}
          className={
            (summary?.cameras_with_issues || 0) > 0 ? "border-amber-500/30" : ""
          }
        />
        <MetricCard
          title="Frequent Parker Misses"
          value={formatNumber(summary?.total_frequent_parker_misses || 0)}
          description="Known patterns broken"
          icon={Car}
          loading={isLoading}
        />
        <MetricCard
          title="One-Way Plates"
          value={formatNumber(summary?.total_one_way_plates || 0)}
          description="Only enter OR exit"
          icon={ArrowRight}
          loading={isLoading}
        />
      </div>

      {/* Orphan Distribution Insight */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Orphan Record Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div
                className={`w-3 h-3 rounded-full ${
                  summary.orphan_concentration === "dispersed"
                    ? "bg-amber-500"
                    : "bg-blue-500"
                }`}
              />
              <div>
                <p className="font-medium">
                  {summary.orphan_concentration === "dispersed"
                    ? "Orphans are spread across many plates"
                    : "Orphans concentrated in specific plates"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {summary.orphan_concentration_detail}
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">
                  Total orphans in period:
                </span>
                <span className="ml-2 font-medium">
                  {formatNumber(summary.total_orphans_in_period)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  Unique plates affected:
                </span>
                <span className="ml-2 font-medium">
                  {formatNumber(summary.unique_orphan_plates)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Camera Health Report */}
      <Card>
        <CardHeader>
          <CardTitle>Camera Health Report</CardTitle>
          <p className="text-sm text-muted-foreground">
            Cameras with significant miss rates, based on frequent parker
            behavior
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-pulse text-muted-foreground">
                Loading...
              </div>
            </div>
          ) : cameraHealth.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <CheckCircle className="w-5 h-5 mr-2 text-emerald-500" />
              No cameras with significant issues in this period
            </div>
          ) : (
            <div className="space-y-4">
              {cameraHealth.map((camera) => {
                const key = `${camera.camera_id}-${camera.zone}-${camera.camera_type}`;
                const isExpanded = expandedCameras.has(key);

                return (
                  <div key={key} className="border rounded-lg overflow-hidden">
                    {/* Camera Header */}
                    <button
                      onClick={() => toggleCamera(key)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <Camera
                              className={`w-4 h-4 ${getStatusColor(camera.status)}`}
                            />
                            <span className="font-medium">
                              Camera {camera.camera_id}
                            </span>
                            <span className="text-muted-foreground">
                              (Zone {camera.zone}, {camera.camera_type})
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {camera.interpretation}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {camera.change_detected && (
                          <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-1 rounded">
                            {camera.change_detected}
                          </span>
                        )}
                        {getStatusBadge(camera.status)}
                        <div className="text-right">
                          <div className="font-bold text-lg">
                            {formatNumber(camera.total_misses)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            missed reads
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 py-4 border-t bg-muted/20">
                        <div className="grid gap-6 lg:grid-cols-2">
                          {/* Weekly Trend Chart */}
                          <div>
                            <h4 className="text-sm font-medium mb-3">
                              Weekly Trend
                            </h4>
                            <div className="h-48">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={camera.weekly_trend}>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    className="stroke-muted"
                                  />
                                  <XAxis
                                    dataKey="week"
                                    className="text-xs fill-muted-foreground"
                                    tickFormatter={(v) =>
                                      new Date(v).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                      })
                                    }
                                  />
                                  <YAxis className="text-xs fill-muted-foreground" />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: "hsl(var(--card))",
                                      border: "1px solid hsl(var(--border))",
                                      borderRadius: "8px",
                                    }}
                                    labelFormatter={(v) =>
                                      `Week of ${new Date(v).toLocaleDateString()}`
                                    }
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="misses"
                                    stroke="#ef4444"
                                    strokeWidth={2}
                                    dot={{ fill: "#ef4444", strokeWidth: 2 }}
                                    name="Missed Reads"
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Details */}
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-medium mb-2">
                                Statistics
                              </h4>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="bg-muted/50 rounded p-2">
                                  <div className="text-muted-foreground">
                                    Total Misses
                                  </div>
                                  <div className="font-bold">
                                    {formatNumber(camera.total_misses)}
                                  </div>
                                </div>
                                <div className="bg-muted/50 rounded p-2">
                                  <div className="text-muted-foreground">
                                    Plates Affected
                                  </div>
                                  <div className="font-bold">
                                    {formatNumber(
                                      camera.unique_plates_affected,
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {camera.example_plates.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium mb-2">
                                  Example Plates
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {camera.example_plates
                                    .slice(0, 5)
                                    .map((plate) => (
                                      <span
                                        key={plate}
                                        className="px-2 py-1 bg-muted rounded text-xs font-mono"
                                      >
                                        {plate}
                                      </span>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  These plates have established patterns but
                                  were missed by this camera
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-Way Plates Report */}
      <Card>
        <CardHeader>
          <CardTitle>One-Way Plate Analysis</CardTitle>
          <p className="text-sm text-muted-foreground">
            Vehicles only seen entering OR exiting - potential wrong-way drivers
            or camera blind spots
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-pulse text-muted-foreground">
                Loading...
              </div>
            </div>
          ) : oneWayReports.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No significant one-way patterns detected
            </div>
          ) : (
            <div className="space-y-4">
              {oneWayReports.map((report, idx) => (
                <div
                  key={`${report.zone}-${report.pattern}-${idx}`}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Zone {report.zone}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            report.pattern === "entry_only"
                              ? "bg-blue-500/10 text-blue-500"
                              : "bg-purple-500/10 text-purple-500"
                          }`}
                        >
                          {report.pattern === "entry_only"
                            ? "Entry Only"
                            : "Exit Only"}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {report.interpretation}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{report.unique_plates}</div>
                      <div className="text-xs text-muted-foreground">
                        plates
                      </div>
                    </div>
                  </div>

                  {report.example_plates.length > 0 && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-2">
                        Example plates:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {report.example_plates.slice(0, 8).map((plate) => (
                          <span
                            key={plate}
                            className="px-2 py-1 bg-muted rounded text-xs font-mono"
                          >
                            {plate}
                          </span>
                        ))}
                        {report.example_plates.length > 8 && (
                          <span className="px-2 py-1 text-xs text-muted-foreground">
                            +{report.example_plates.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
